// frontend/src/components/report/primitives/FindingsList.js
import { useState } from "react";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { SEVERITY_CLS } from "../reportUtils";

// Same grouping PillarFindings used to do inline, plus a severityCounts
// tally across all of pillarKeys combined (used for the summary chip row).
export function groupFindings(findings, pillarKeys) {
  const groups = pillarKeys
    .map((pillar) => ({ pillar, items: findings.filter((f) => f.pillar === pillar) }))
    .filter((g) => g.items.length > 0);
  const severityCounts = { critical: 0, high: 0, medium: 0, low: 0, positive: 0 };
  findings
    .filter((f) => pillarKeys.includes(f.pillar))
    .forEach((f) => {
      const key = severityCounts[f.severity] !== undefined ? f.severity : "low";
      severityCounts[key] += 1;
    });
  return { groups, severityCounts };
}

// The severity filter row's "show only this severity" logic: null/"all"
// leaves every group untouched; any other value keeps only the matching
// items per group and drops a pillar group entirely once it has none left —
// same "pillar with zero matching findings disappears" rule groupFindings
// already applies for pillarKeys.
export function filterFindingsBySeverity(groups, severity) {
  if (!severity || severity === "all") return groups;
  return groups
    .map((g) => ({ ...g, items: g.items.filter((f) => f.severity === severity) }))
    .filter((g) => g.items.length > 0);
}

const CHIP_ORDER = ["critical", "high", "medium", "low"];

// Severity-count summary row + one Accordion item per pillar group. A pillar
// group defaults open only if it contains at least one critical finding —
// spec §5 ("sadece kritik grubu varsayılan açık") — recomputed for whichever
// severity filter is currently active (the Accordion is remounted via `key`
// on filter change since Radix's defaultValue is read only once per mount).
export function FindingsList({ findings, pillars, pillarMax, pillarKeys, pillarLabels, severityLabels, headingText }) {
  const [filter, setFilter] = useState("all");
  const { groups: allGroups, severityCounts } = groupFindings(findings, pillarKeys);
  if (!allGroups.length) return null;
  const groups = filterFindingsBySeverity(allGroups, filter);
  const defaultOpen = groups.filter((g) => g.items.some((f) => f.severity === "critical")).map((g) => g.pillar);
  const availableSeverities = CHIP_ORDER.filter((s) => severityCounts[s] > 0);

  return (
    <div className="mt-6">
      <div className="mb-3 flex items-center gap-2">
        <h2 className="font-heading text-lg font-semibold">{headingText}</h2>
        <div className="flex flex-wrap gap-1.5">
          {availableSeverities.map((s) => (
            <span key={s} className={`rounded-full border px-2 py-0.5 text-xs font-semibold ${SEVERITY_CLS[s]}`}>
              {severityCounts[s]} {severityLabels[s]}
            </span>
          ))}
        </div>
      </div>

      {availableSeverities.length > 0 && (
        <div className="mb-3 flex flex-wrap gap-2" role="group" aria-label={headingText}>
          <button
            type="button"
            onClick={() => setFilter("all")}
            aria-pressed={filter === "all"}
            className={`rounded-full border px-3 py-1 text-sm font-medium transition-colors ${
              filter === "all" ? "border-primary bg-primary/10 text-primary" : "border-input text-muted-foreground hover:bg-secondary"
            }`}
          >
            {severityLabels.all} ({findings.filter((f) => pillarKeys.includes(f.pillar)).length})
          </button>
          {availableSeverities.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setFilter(s)}
              aria-pressed={filter === s}
              className={`rounded-full border px-3 py-1 text-sm font-medium transition-colors ${
                filter === s ? "border-primary bg-primary/10 text-primary" : "border-input text-muted-foreground hover:bg-secondary"
              }`}
            >
              {severityLabels[s]} ({severityCounts[s]})
            </button>
          ))}
        </div>
      )}

      <Accordion key={filter} type="multiple" defaultValue={defaultOpen} className="space-y-2">
        {groups.map(({ pillar, items }) => (
          <AccordionItem key={pillar} value={pillar} className="rounded-xl border px-3.5">
            <AccordionTrigger>
              <span className="flex items-center gap-2 text-base font-semibold">
                {pillarLabels[pillar]}
                <span className="rounded-full bg-secondary px-2 py-0.5 text-xs tabular-nums text-muted-foreground">
                  {items.length}
                </span>
              </span>
              {pillars[pillar] != null && (
                <span className="mr-2 text-sm tabular-nums text-muted-foreground">
                  {pillars[pillar]}/{pillarMax[pillar]}
                </span>
              )}
            </AccordionTrigger>
            <AccordionContent>
              <div className="space-y-2.5">
                {items.map((f, i) => (
                  <div key={i} className={`rounded-xl border p-3.5 ${SEVERITY_CLS[f.severity] || SEVERITY_CLS.low}`}>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="rounded-full border bg-white/60 px-2 py-0.5 text-xs font-semibold">
                        {severityLabels[f.severity] || severityLabels.low}
                      </span>
                      <span className="text-base font-semibold">{f.title}</span>
                    </div>
                    <p className="mt-1.5 text-base opacity-90">{f.evidence}</p>
                    {f.service && <p className="mt-1.5 text-sm font-medium opacity-75">→ {f.service}</p>}
                  </div>
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  );
}
